import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  BackendUpdateResponse,
  DatastoreCollection,
  HourlyContract,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontSize, FontType } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  template: `
    <fl-bit flTrackingSection="ProjectEndBillingModal">
      <fl-heading
        *ngIf="!(isTokenProject$ | async); else endAutoApprovalHeading"
        i18n="End billing modal heading"
        [size]="HeadingSize.XSMALL"
        [headingType]="HeadingType.H4"
        [flMarginBottom]="Margin.SMALL"
      >
        End Billing?
      </fl-heading>
      <ng-template #endAutoApprovalHeading>
        <fl-heading
          i18n="End Automatic Approval modal heading"
          [size]="HeadingSize.XSMALL"
          [headingType]="HeadingType.H4"
          [flMarginBottom]="Margin.SMALL"
        >
          End Automatic Approval?
        </fl-heading>
      </ng-template>
      <fl-text
        *ngIf="!(isTokenProject$ | async); else submitForApprovalText"
        i18n="End billing modal information text"
        [size]="FontSize.MID"
        [fontType]="FontType.PARAGRAPH"
        [flMarginBottom]="Margin.SMALL"
      >
        {{ displayName }} will not be able to track any more hours. Any
        remaining hours will be invoiced right away.
      </fl-text>
      <ng-template #submitForApprovalText>
        <fl-text
          i18n="End auto approval modal information text"
          [size]="FontSize.MID"
          [fontType]="FontType.PARAGRAPH"
          [flMarginBottom]="Margin.SMALL"
        >
          {{ displayName }} will not be able to track any more hours. Any
          remaining hours will be submitted for your approval right away.
        </fl-text>
      </ng-template>

      <fl-banner-alert
        *ngIf="(endBillingPromise | async)?.status === 'error'"
        i18n="End billing error"
        [type]="BannerAlertType.ERROR"
        [flMarginBottom]="Margin.SMALL"
      >
        Something went wrong when ending the billing. Please contact
        support@freelancer.com
        <ng-container *ngIf="(endBillingPromise | async)?.requestId">
          with requestId: {{ (endBillingPromise | async)?.requestId }}
        </ng-container>
      </fl-banner-alert>

      <fl-bit class="Buttons">
        <fl-button
          flTrackingLabel="EndBillingModalEndButton"
          [busy]="endBillingPromise && (endBillingPromise | async) === null"
          [color]="ButtonColor.SECONDARY"
          [flMarginRight]="Margin.XSMALL"
          [size]="ButtonSize.SMALL"
          (click)="endBilling()"
        >
          <ng-container
            *ngIf="!(isTokenProject$ | async); else endAutoApprovalButton"
            i18n="End billing button"
          >
            End Billing
          </ng-container>
          <ng-template #endAutoApprovalButton>
            <ng-container i18n="End auto approval button">
              End Automatic Approval
            </ng-container>
          </ng-template>
        </fl-button>

        <fl-button
          flTrackingLabel="EndBillingModalBackButton"
          i18n="Close modal button"
          [color]="ButtonColor.DEFAULT"
          [size]="ButtonSize.SMALL"
          (click)="close()"
        >
          Back
        </fl-button>
      </fl-bit>
    </fl-bit>
  `,
  styleUrls: ['./project-end-billing-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectEndBillingModalComponent {
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  FontType = FontType;
  FontSize = FontSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  BannerAlertType = BannerAlertType;
  Margin = Margin;

  @Input() displayName: string;
  @Input() hourlyContractsCollection: DatastoreCollection<'hourlyContracts'>;
  @Input() hourlyContract$: Rx.Observable<HourlyContract>;
  @Input() isTokenProject$: Rx.Observable<boolean>;

  endBillingPromise: Promise<BackendUpdateResponse<'hourlyContracts'>>;

  constructor(private modalRef: ModalRef<ProjectEndBillingModalComponent>) {}

  endBilling() {
    this.endBillingPromise = this.hourlyContract$
      .pipe(take(1))
      .toPromise()
      .then(hourlyContract => {
        if (hourlyContract && this.hourlyContractsCollection) {
          const contractId = hourlyContract.id;

          return this.hourlyContractsCollection.update(contractId, {
            timeTrackingStopped: hourlyContract.timeTrackingStopped,
          });
        }

        return Promise.resolve({
          status: 'error',
          errorCode: 'UNKNOWN_ERROR',
        } as const);
      })
      .then(res => {
        if (res.status === 'success') {
          this.close();
        }
        return res;
      });
  }

  close() {
    this.modalRef.close();
  }
}
